import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";

export interface PublicAgentData {
  agent: any;
  organization: any | null;
  isLoading: boolean;
  error: string | null;
}

export const usePublicAgent = (
  publicId: string | undefined,
  agentUrl: string | undefined,
): PublicAgentData => {
  const [agent, setAgent] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get("embed") === "true";

  useEffect(() => {
    const fetchAgentAndOrganization = async () => {
      console.log("usePublicAgent: Received publicId parameter:", publicId);

      if (!publicId) {
        console.error("usePublicAgent: Missing publicId parameter in URL");
        setError("No agent ID provided");
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching agent with public_id:", publicId);

        // Use the Edge Function to ensure the same behavior as the API endpoint
        const { data, error: functionError } = await supabase.functions.invoke(
          "get-public-agent",
          {
            body: { publicId },
          },
        );

        if (functionError) {
          console.error("Error from Edge Function:", functionError);
          throw new Error(
            functionError.message || "Failed to fetch agent details",
          );
        }

        if (!data || !data.agent) {
          console.error("Invalid response from Edge Function:", data);
          throw new Error("Agent not found or inactive");
        }

        console.log("Found agent:", data.agent);
        setAgent(data.agent);

        if (data.organization) {
          console.log("Found organization:", data.organization);
          // Only validate agent URL if one was provided (skip for preview pages)
          if (
            agentUrl !== undefined &&
            data.organization?.agent_url !== agentUrl
          ) {
            setError("Agent URL does not match.");
            setIsLoading(false);
            return;
          }
          setOrganization(data.organization);
        }
      } catch (err: any) {
        console.error("Error in fetchAgentAndOrganization:", err);
        setError(err.message || "Failed to load the requested agent");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentAndOrganization();
  }, [publicId]);

  return { agent, organization, isLoading, error };
};
